import React, { useEffect, useRef, useState } from 'react';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import {
  getInstallersForWorkDays,
  getWorkDays,
  getWorkMonths,
  getWorkWeeks,
  InstallationTimeField,
  toWorkDays,
} from 'services/offers/workDays';
import { Col, Row } from 'reactstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import getFormStateHandler from 'services/forms/getFormStateHandler';
import InputLabel from 'components/layout/forms/InputLabel';
import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import translations from 'services/translations/translations';
import { CONSIGNMENT, H } from 'services/enums/units';

import {
  ContractLockField,
  getAverageHourlyEarningsFor,
  getContractLock,
  getContractLockRefusal,
  getInstallersFor,
  IContractLock,
  withContractLock,
} from 'services/offers/contractLock';
import { useCurrency } from 'services/offers/currency';
import {
  contractFields,
  getContractValues,
} from 'services/offers/installationSections';
import getVersionInstallationContract from './getVersionInstallationContract';
import IVersion from '../IVersion';

// At most two decimals without trailing zeros, with a decimal comma (e.g. 2 or 2,5)
const formatShortNumber = (value: number): string =>
  String(Math.round(value * 100) / 100).replace('.', ',');

const InstallationContract = ({
  locked,
  productUnit,
  quantity,
  section = null,
  setVersion,
  title,
  version,
}: {
  locked: boolean;
  productUnit: string;
  quantity: number;
  // The summary row of subcontracting or own production; none for the installation contract
  section?: null | string;
  setVersion: (version: IVersion) => void;
  // The heading of the summary row of a section
  title?: string;
  version: IVersion;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers]);
  const currency = useCurrency();

  const handleFormState = getFormStateHandler(version, setVersion);

  const offerIsLocked = () => locked;

  const installationCategories = useSelector(
    (state: any) => state.products.installationCategories
  );

  const installationContract = getVersionInstallationContract(
    version,
    quantity,
    installationCategories,
    section
  );

  // The fields of the version of this summary row
  const fields = contractFields(section);
  const contractValues = getContractValues(version, section);
  const idPrefix = section ? `${section}-` : '';
  const contractLabel: string =
    title ?? `${t(`${translations.calculationWindow}:Installation contract`)}`;

  /*
   * Installers and installation time (8 h a day, 5 days a week, 21 days a month). KTA gives
   * the total hours and the installers share them into work days, weeks and months. Typing
   * the work days, weeks or months changes the installers, the hours stay. The typed text is
   * kept as it is until the KTA, the installers or another time is changed. The installers
   * are saved with the version.
   */
  const installers = contractValues.installerCount;
  const setInstallers = (installerCount: string) =>
    setVersion({ ...version, [fields.installerCount]: installerCount });
  const [typedTime, setTypedTime] = useState<{
    field: InstallationTimeField;
    text: string;
  } | null>(null);
  // Set when typing the time changes the KTA, so the hours change but the typed time stays
  const keepTypedTime = useRef(false);

  // A time typed for other hours or for another alternative is not shown any more
  useEffect(() => {
    if (keepTypedTime.current) {
      keepTypedTime.current = false;
      return;
    }
    setTypedTime(null);
  }, [installationContract.totalHours, version.id, version.name]);

  /*
   * One of KTA, the installers and the work time can be locked (contract = KTA × installers ×
   * work days × 8 h): changing another changes the one left free. The lock is saved with
   * the version; the locked time is kept in work days.
   */
  const lock = getContractLock(version.contractLocks, section);
  const setLock = (newLock: IContractLock | null) =>
    setVersion({
      ...version,
      contractLocks: withContractLock(version.contractLocks, section, newLock),
    });

  const contract = installationContract.installationsInstallationPriceSum;
  const averageHourlyEarnings = numericStringToFloat(
    contractValues.averageHourlyEarnings
  );
  const installerCount = numericStringToFloat(installers);

  const workDays = getWorkDays(installationContract.totalHours, installerCount);

  // A batch (erä) is one whole: the contract per unit equals the whole contract and
  // units per hour say nothing, so those are not shown
  const showPerUnit = productUnit !== CONSIGNMENT;

  // The other times follow the time typed by the user, or the locked time
  const getShownWorkDays = () => {
    if (lock?.field === 'time') {
      return lock.workDays;
    }

    return typedTime === null
      ? workDays
      : toWorkDays(typedTime.field, numericStringToFloat(typedTime.text));
  };
  const shownWorkDays = getShownWorkDays();

  // With the time locked, a change of the rows changes the installers
  useEffect(() => {
    if (lock?.field !== 'time' || offerIsLocked()) {
      return;
    }

    const installersForTime = getInstallersFor(
      contract,
      averageHourlyEarnings,
      lock.workDays
    );
    // Compared as shown (two decimals), so opening the window does not change a version
    // saved with the same installers
    if (
      installersForTime > 0 &&
      formatShortNumber(installersForTime) !== formatShortNumber(installerCount)
    ) {
      setInstallers(formatShortNumber(installersForTime));
    }
  }, [contract]);

  const getTimeValue = (field: InstallationTimeField, value: number) =>
    typedTime !== null && typedTime.field === field
      ? typedTime.text
      : formatGroupedNumber(value);

  const handleAverageHourlyEarnings = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTypedTime(null);
    const { value } = event.target;

    // The time locked: the installers follow the KTA
    const installersForTime =
      lock?.field === 'time'
        ? getInstallersFor(contract, numericStringToFloat(value), lock.workDays)
        : 0;
    if (installersForTime > 0) {
      setVersion({
        ...version,
        [fields.averageHourlyEarnings]: value,
        [fields.installerCount]: formatShortNumber(installersForTime),
      });
      return;
    }

    handleFormState(event);
  };

  const handleInstallers = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTypedTime(null);
    const { value } = event.target;

    // The time locked: the KTA follows the installers
    const averageHourlyEarningsForTime =
      lock?.field === 'time'
        ? getAverageHourlyEarningsFor(
            contract,
            numericStringToFloat(value),
            lock.workDays
          )
        : 0;
    if (averageHourlyEarningsForTime > 0) {
      setVersion({
        ...version,
        [fields.averageHourlyEarnings]: formatShortNumber(
          averageHourlyEarningsForTime
        ),
        [fields.installerCount]: value,
      });
      return;
    }

    setInstallers(value);
  };

  const getTimeHandler =
    (field: InstallationTimeField) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setTypedTime({ field, text: value });
      const typedWorkDays = toWorkDays(field, numericStringToFloat(value));

      // The installers locked: the KTA follows the time
      if (lock?.field === 'installers') {
        const averageHourlyEarningsForTime = getAverageHourlyEarningsFor(
          contract,
          installerCount,
          typedWorkDays
        );
        const shownAverageHourlyEarnings = formatShortNumber(
          averageHourlyEarningsForTime
        );
        // The hours change only with the KTA; then the typed time is kept
        if (
          averageHourlyEarningsForTime > 0 &&
          numericStringToFloat(shownAverageHourlyEarnings) !==
            averageHourlyEarnings
        ) {
          keepTypedTime.current = true;
          setVersion({
            ...version,
            [fields.averageHourlyEarnings]: shownAverageHourlyEarnings,
          });
        }
        return;
      }

      const installersForTime = getInstallersForWorkDays(
        installationContract.totalHours,
        typedWorkDays
      );

      if (installersForTime > 0) {
        setInstallers(formatShortNumber(installersForTime));
      }
    };

  // The lock inside a field; a second lock or one without a value is not allowed
  const getLockButton = (field: ContractLockField) => {
    if (offerIsLocked()) {
      return undefined;
    }

    const isLocked = lock?.field === field;
    const refusal = isLocked
      ? null
      : getContractLockRefusal(field, lock?.field ?? null, {
          averageHourlyEarnings,
          installers: installerCount,
          workDays: shownWorkDays,
        });

    return (
      <button
        aria-pressed={isLocked}
        className={`contract-lock${isLocked ? ' contract-lock-on' : ''}`}
        disabled={refusal !== null}
        onClick={() => {
          setTypedTime(null);
          setLock(isLocked ? null : { field, workDays: shownWorkDays });
        }}
        title={t(
          `${translations.calculationWindow}:contractLock.${
            refusal ?? (isLocked ? 'Unlock' : 'Lock')
          }`
        )}
        type="button"
      >
        {isLocked ? '🔒' : '🔓'}
      </button>
    );
  };

  return (
    <div className="calculation-sum-row">
      <h2>{contractLabel}</h2>
      <Row>
        <Col>
          <InputLabel>
            {t(`${translations.calculationWindow}:Contract price`)}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {contractLabel} {currency}
          </InputLabel>
        </Col>
        {showPerUnit ? (
          <Col>
            <InputLabel>
              {`${contractLabel} ${currency}`}/
              {t(`${translations.common}:units:${productUnit}`)}
            </InputLabel>
          </Col>
        ) : null}
        <Col>
          <InputLabel>
            {t(
              `${translations.calculationWindow}:average-hourly-earnings-short`
            )}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {t(`${translations.calculationWindow}:Total hours`)}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {t(`${translations.calculationWindow}:Installers`)}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {t(`${translations.calculationWindow}:Work days`)}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {t(`${translations.calculationWindow}:Weeks`)}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {t(`${translations.calculationWindow}:Months`)}
          </InputLabel>
        </Col>
        {showPerUnit ? (
          <Col>
            <InputLabel>
              {t(`${translations.common}:units:${productUnit}`)}/
              {t(`${translations.common}:units:${H}`)}
            </InputLabel>
          </Col>
        ) : null}
        <Col className="key-figure">
          <InputLabel>
            {`${t(`${translations.offers}:Contribution margin`)} ${currency}`}/
            {t(`${translations.common}:units:${H}`)}
          </InputLabel>
        </Col>
      </Row>
      <Row>
        <Col>
          <NumberWithUnitInputField
            disabled
            id={`${idPrefix}intallations-expense-sum`}
            unit={currency}
            value={formatGroupedNumber(
              installationContract.installationsExpenseTotal
            )}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled
            id={`${idPrefix}installation-contract-eur`}
            unit={currency}
            value={formatGroupedNumber(
              installationContract.installationsInstallationPriceSum
            )}
          />
        </Col>
        {showPerUnit ? (
          <Col>
            <NumberWithUnitInputField
              disabled
              id={`${idPrefix}installation-contract-eur-for-unit`}
              unit={`${currency}/${t(
                `${translations.common}:units:${productUnit}`
              )}`}
              value={formatGroupedNumber(
                installationContract.installationsUnitInstallationPrice
              )}
            />
          </Col>
        ) : null}
        <Col>
          <NumberWithUnitInputField
            disabled={
              offerIsLocked() || lock?.field === 'averageHourlyEarnings'
            }
            id={`${idPrefix}average-hourly-earnings`}
            name={fields.averageHourlyEarnings}
            onChange={handleAverageHourlyEarnings}
            startAdornment={getLockButton('averageHourlyEarnings')}
            unit={currency}
            value={contractValues.averageHourlyEarnings}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled
            id={`${idPrefix}total-hours`}
            unit={H}
            value={formatGroupedNumber(installationContract.totalHours)}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled={offerIsLocked() || lock?.field === 'installers'}
            id={`${idPrefix}installers`}
            name="installers"
            onChange={handleInstallers}
            startAdornment={getLockButton('installers')}
            unit={t(`${translations.calculationWindow}:installers-short`)}
            value={installers}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled={offerIsLocked() || lock?.field === 'time'}
            id={`${idPrefix}work-days`}
            name="workDays"
            onChange={getTimeHandler('days')}
            startAdornment={getLockButton('time')}
            unit={t(`${translations.calculationWindow}:work-days-short`)}
            value={getTimeValue('days', shownWorkDays)}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled={offerIsLocked() || lock?.field === 'time'}
            id={`${idPrefix}work-weeks`}
            name="workWeeks"
            onChange={getTimeHandler('weeks')}
            startAdornment={getLockButton('time')}
            unit={t(`${translations.calculationWindow}:weeks-short`)}
            value={getTimeValue('weeks', getWorkWeeks(shownWorkDays))}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled={offerIsLocked() || lock?.field === 'time'}
            id={`${idPrefix}work-months`}
            name="workMonths"
            onChange={getTimeHandler('months')}
            startAdornment={getLockButton('time')}
            unit={t(`${translations.calculationWindow}:months-short`)}
            value={getTimeValue('months', getWorkMonths(shownWorkDays))}
          />
        </Col>
        {showPerUnit ? (
          <Col>
            <NumberWithUnitInputField
              disabled
              id={`${idPrefix}unit-for-h`}
              unit={t(`${translations.common}:units:${productUnit}`)}
              value={formatGroupedNumber(installationContract.unitForH)}
            />
          </Col>
        ) : null}
        <Col className="key-figure">
          <NumberWithUnitInputField
            disabled
            id={`${idPrefix}contribution-margin-eur-for-h`}
            unit={currency}
            value={formatGroupedNumber(
              installationContract.contributionMarginEurForH
            )}
          />
        </Col>
      </Row>
    </div>
  );
};

export default InstallationContract;
